import type { Role } from '@servicetim/shared';
import { useCreateAssignment, useGroups } from '@servicetim/ui/api';
import {
  AsyncPicker,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ProblemBanner,
  toast,
  useUserOptionLoader,
} from '@servicetim/ui/components';
import { useEffect, useId, useState } from 'react';
import { problemDetail } from '../lib/field-errors';
import { FormRow } from '../ui/form-row';
import { SimpleSelect } from '../ui/simple-select';

export interface AssignmentDialogProps {
  projectId: string;
  roles: readonly Role[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type GranteeKind = 'user' | 'group';

/** Grant a project role to a user or a group. */
export function AssignmentDialog({ projectId, roles, open, onOpenChange }: AssignmentDialogProps) {
  const id = useId();
  const create = useCreateAssignment(projectId);
  const loadUsers = useUserOptionLoader();
  const groups = useGroups({ pageSize: 200, enabled: open });
  const [kind, setKind] = useState<GranteeKind>('user');
  const [userId, setUserId] = useState<string | undefined>();
  const [groupId, setGroupId] = useState<string>('');
  const [roleId, setRoleId] = useState<string | undefined>();
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset when opened
  useEffect(() => {
    if (open) {
      setKind('user');
      setUserId(undefined);
      setGroupId('');
      setRoleId(roles[0]?.id);
      create.reset();
    }
  }, [open]);

  const duplicate = problemDetail(create.error) === 'assignment.duplicate';
  const grantee = kind === 'user' ? userId : groupId.trim() || undefined;
  const canSubmit = roleId !== undefined && grantee !== undefined && !create.isPending;

  const submit = async () => {
    if (!roleId || !grantee) return;
    try {
      await create.mutateAsync(
        kind === 'user' ? { roleId, userId: grantee } : { roleId, groupId: grantee },
      );
      toast.success('Role assigned');
      onOpenChange(false);
    } catch {
      // banner
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>Assign a role</DialogTitle>
          <DialogDescription>
            Users hold the role directly; group members hold it through the group.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {create.error ? (
            <ProblemBanner
              error={create.error}
              size="sm"
              title={duplicate ? 'This grantee already holds the role' : undefined}
            />
          ) : null}
          <fieldset className="space-y-1.5">
            <legend className="font-medium text-fg text-sm">Grantee</legend>
            <div className="flex gap-4 text-sm">
              {(['user', 'group'] as const).map((k) => (
                <label key={k} className="flex items-center gap-1.5">
                  <input
                    type="radio"
                    name={`${id}-kind`}
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  {k === 'user' ? 'User' : 'Group'}
                </label>
              ))}
            </div>
          </fieldset>
          {kind === 'user' ? (
            <FormRow label="User" htmlFor={`${id}-user`} required>
              <AsyncPicker
                id={`${id}-user`}
                value={userId}
                onChange={setUserId}
                load={loadUsers}
                placeholder="Search people…"
              />
            </FormRow>
          ) : groups.error ? (
            <FormRow
              label="Group id"
              htmlFor={`${id}-group`}
              required
              hint="The groups list needs tenant admin rights; paste the group id instead."
            >
              <Input
                id={`${id}-group`}
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                className="font-mono"
              />
            </FormRow>
          ) : (
            <FormRow label="Group" htmlFor={`${id}-group`} required>
              <SimpleSelect
                id={`${id}-group`}
                value={groupId || undefined}
                onChange={(next) => setGroupId(next ?? '')}
                options={groups.items.map((g) => ({
                  value: g.id,
                  label: g.memberCount !== undefined ? `${g.name} (${g.memberCount})` : g.name,
                }))}
                placeholder={groups.isPending ? 'Loading groups…' : 'Choose a group…'}
                disabled={groups.isPending}
              />
            </FormRow>
          )}
          <FormRow label="Role" htmlFor={`${id}-role`} required>
            <SimpleSelect
              id={`${id}-role`}
              value={roleId}
              onChange={setRoleId}
              options={roles.map((r) => ({
                value: r.id,
                label: r.projectId === null ? `${r.name} (tenant-wide)` : r.name,
              }))}
            />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              Assign
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
